import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { LayoutConfig } from "../../types";
import {
  GripVertical,
  Trash2,
  Copy,
  EyeOff,
  Eye,
  Plus,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useEditorStore, Component } from "../../stores/editorStore";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const getComponentId = (component: any): string =>
  component?.name_id || component?.name || "";

// Modal de confirmação
function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  componentName,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  componentName: string;
}) {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-backdrop flex items-center justify-center z-[9999]">
      <div className="glass-modal modal-enter rounded-xl p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Confirmar remoção
        </h3>
        <p className="text-gray-600 mb-4">
          Tem certeza que deseja remover o componente "{componentName}"?
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Remover
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Componente SortableItem
function SortableItem({
  component,
  selectedComponent,
  onComponentSelect,
  onRemove,
  onClone,
  onToggleHide,
  isLoading,
  canEditComponent,
  isSortable = true,
}: {
  component: Component;
  selectedComponent: string | null;
  onComponentSelect: (name: string) => void;
  onRemove: (name: string) => void;
  onClone: (name: string) => void;
  onToggleHide: (name: string) => void;
  isLoading: boolean;
  canEditComponent: (type: string) => boolean;
  isSortable?: boolean;
}) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getComponentId(component),
    disabled: !isSortable, // Desabilitar drag para componentes não ordenáveis
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmModal(true);
  };

  const handleConfirmRemove = () => {
    onRemove(getComponentId(component));
    setShowConfirmModal(false);
  };

  const handleCancelRemove = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`flex flex-col items-start space-x-2 py-2 px-1 rounded-md border cursor-pointer transition-colors ${
          selectedComponent === getComponentId(component)
            ? "border-primary-500 bg-primary-50"
            : "border-gray-200 hover:border-gray-300"
        } ${isDragging ? "shadow-lg rotate-2" : ""}`}
        onClick={() => onComponentSelect(getComponentId(component))}
      >
        <div className="flex items-center space-x-1">
          {isSortable ? (
            <div
              {...attributes}
              {...listeners}
              className="flex items-center h-full"
            >
              <GripVertical className="w-4 h-12 text-gray-400" />
            </div>
          ) : (
            <div className="w-4 h-12" />
          )}
          <div className="flex flex-col">
            <span className="flex-1 text-xs font-medium text-primary-500">
              {component.label}
            </span>
            <span className="flex-1 text-xs font-thin text-gray-700">
              {component.component}
            </span>
            <div className="flex items-center space-x-2">
              {canEditComponent(component.component) &&
                component.can_clone !== false &&
                component.scope !== "global" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) onClone(getComponentId(component));
                    }}
                    disabled={isLoading}
                    className={`p-1 transition-colors ${
                      isLoading
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-400 hover:text-primary-500"
                    }`}
                    title={
                      isLoading
                        ? "Aguarde o processamento anterior"
                        : "Clonar componente"
                    }
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              {component.can_hide !== false && component.scope === "global" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isLoading) onToggleHide(getComponentId(component));
                  }}
                  disabled={isLoading}
                  className={`p-1 transition-colors ${
                    isLoading
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-400 hover:text-yellow-600"
                  }`}
                  title={
                    isLoading
                      ? "Aguarde o processamento anterior"
                      : component.hide
                        ? "Mostrar componente"
                        : "Ocultar componente"
                  }
                >
                  {component.hide ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </button>
              )}
              {canEditComponent(component.component) &&
                component.can_remove !== false &&
                component.scope !== "global" && (
                  <button
                    onClick={handleRemoveClick}
                    disabled={isLoading}
                    className={`p-1 transition-colors ${
                      isLoading
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-400 hover:text-red-500"
                    }`}
                    title={
                      isLoading
                        ? "Aguarde o processamento anterior"
                        : "Remover componente"
                    }
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        componentName={component.label}
      />
    </>
  );
}

interface ComponentPanelProps {
  layoutConfig: LayoutConfig | null;
  onClose?: () => void;
}

// Função auxiliar para renderizar a lista de itens
function ComponentList({
  title,
  count,
  items,
  sortableItems,
  selectedComponent,
  onSelect,
  onRemove,
  onClone,
  onToggleHide,
  isLoading,
  canEditFn,
  isGlobal = false,
}: {
  title: string;
  count: number;
  items: Component[];
  sortableItems: string[];
  selectedComponent: string | null;
  onSelect: (name: string) => void;
  onRemove: (name: string) => void;
  onClone: (name: string) => void;
  onToggleHide: (name: string) => void;
  isLoading: boolean;
  canEditFn: (type: string) => boolean;
  isGlobal?: boolean;
}) {
  const [isCollapsed, setIsCollapsed] = useState(isGlobal);

  if (items.length === 0) return null;

  // Separar fixos e ordenáveis
  const fixedItems = items.filter((c) => c.can_order === false);
  const reorderableItems = items.filter((c) => c.can_order !== false);

  return (
    <div className="mb-6">
      <div
        className={`flex items-center justify-between mb-3 transition-colors ${isGlobal ? "cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded select-none" : ""}`}
        onClick={() => {
          if (isGlobal) {
            setIsCollapsed(!isCollapsed);
          }
        }}
      >
        <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2 justify-between w-full">
          <div className="flex items-center space-x-2">
            {isGlobal && (
              <span className="text-gray-400">
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </span>
            )}
            <span>{title}</span>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded bg-gray-100 text-gray-500}`}
          >
            {count}
          </span>
        </h4>
      </div>

      {!isCollapsed && (
        <div className="space-y-2 property-expand origin-top overflow-hidden">
          {/* Itens Fixos */}
          {fixedItems.map((component) => (
            <SortableItem
              key={getComponentId(component)}
              component={component}
              selectedComponent={selectedComponent}
              onComponentSelect={onSelect}
              onRemove={onRemove}
              onClone={onClone}
              onToggleHide={onToggleHide}
              isLoading={isLoading}
              canEditComponent={canEditFn}
              isSortable={false}
            />
          ))}

          {/* Separador se houver ambos */}
          {fixedItems.length > 0 && reorderableItems.length > 0 && (
            <div className="my-2 border-b border-gray-100" />
          )}

          {/* Itens Ordenáveis */}
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {reorderableItems.map((component) => (
                <SortableItem
                  key={getComponentId(component)}
                  component={component}
                  selectedComponent={selectedComponent}
                  onComponentSelect={onSelect}
                  onRemove={onRemove}
                  onClone={onClone}
                  onToggleHide={onToggleHide}
                  isLoading={isLoading}
                  canEditComponent={canEditFn}
                  isSortable={true}
                />
              ))}
            </div>
          </SortableContext>
        </div>
      )}
    </div>
  );
}

export default function ComponentPanel({
  layoutConfig,
  onClose,
}: ComponentPanelProps) {
  const {
    components,
    registry,
    selectedComponent,
    isLoading,
    error,
    loadComponents,
    loadRegistry,
    isLoadingRegistry,
    addComponent,
    addFromRegistry,
    removeComponent,
    reorderComponents,
    cloneComponent,
    toggleHideComponent,
    selectComponent,
  } = useEditorStore();

  useEffect(() => {
    loadRegistry();
  }, [loadRegistry]);

  const canEditComponent = (componentType: string) => {
    const nonEditableTypes = ["spot_carousel"];
    return !nonEditableTypes.includes(componentType);
  };

  const handleRemoveComponent = (componentName: string) => {
    removeComponent(componentName);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (isLoading || !over || active.id === over.id) return;

    // Encontrar os componentes
    const activeComponent = components.find((c) => getComponentId(c) === active.id);
    const overComponent = components.find((c) => getComponentId(c) === over.id);

    if (!activeComponent || !overComponent) return;

    const activeScope = activeComponent.scope || "page";
    const overScope = overComponent.scope || "page";

    // Bloquear drag entre escopos diferentes
    if (activeScope !== overScope) return;

    // Calcular índices baseados APENAS nos componentes ordenáveis
    const allSortable = components.filter((c) => c.can_order !== false);
    const oldIndex = allSortable.findIndex((c) => getComponentId(c) === active.id);
    const newIndex = allSortable.findIndex((c) => getComponentId(c) === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      reorderComponents(oldIndex, newIndex);
    }
  };

  // Separar componentes por escopo
  // Globais sempre aparecem (podem estar ocultos via 'hide', mas visualmente aparecem na lista com ícone de olho riscado)
  const globalComponents = components.filter((c) => c.scope === "global");

  // Page components: se 'hide' for true, é porque foi removido LOGICAMENTE?
  // O requisito diz: "ao invés de termos os componentes com hidden, não vamos mais ter essa opção... remover ele apenas removemos ele da home"
  // Então a lista de componentes da página deve mostrar APENAS o que NÃO está hidden?
  // Ou o 'removeComponent' realmente deleta do array?
  // No store, 'removeComponent' filtra e remove do array. Então não existe 'hide: true' para page components na lista.
  const pageComponents = components.filter((c) => c.scope !== "global"); // page ou undefined
  const normalizeInstanceBaseName = (name: string) =>
    name.replace(/_(\d{10,})(?:_[a-z0-9]+)?$/i, "");
  const hasPageInstanceForRegistryItem = (item: Component) => {
    const registryBaseName = normalizeInstanceBaseName(
      item.name_id || item.name || item.label || item.component,
    );
    return pageComponents.some(
      (component) =>
        component.component === item.component &&
        normalizeInstanceBaseName(getComponentId(component)) === registryBaseName,
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Componentes</h3>
          <p className="text-sm text-gray-500">Arraste para reordenar</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Fechar</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Lista de Conteúdo */}
      <div className="flex-1 px-4 py-4 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
            {error}
          </div>
        )}

        {/* --- 2. Lista de Componentes na Página (Drag & Drop) --- */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* Globais */}
          <ComponentList
            title="Globais"
            count={globalComponents.length}
            items={globalComponents}
            sortableItems={globalComponents
              .filter((c) => c.can_order !== false)
              .map((c) => getComponentId(c))}
            selectedComponent={selectedComponent}
            onSelect={selectComponent}
            onRemove={handleRemoveComponent}
            onClone={cloneComponent}
            onToggleHide={toggleHideComponent}
            isLoading={isLoading}
            canEditFn={canEditComponent}
            isGlobal={true}
          />

          {/* Página */}
          <ComponentList
            title="Componentes da Página"
            count={pageComponents.length}
            items={pageComponents}
            sortableItems={pageComponents
              .filter((c) => c.can_order !== false)
              .map((c) => getComponentId(c))}
            selectedComponent={selectedComponent}
            onSelect={selectComponent}
            onRemove={handleRemoveComponent}
            onClone={cloneComponent}
            onToggleHide={toggleHideComponent}
            isLoading={isLoading}
            canEditFn={canEditComponent}
            isGlobal={false}
          />
        </DndContext>

        {/* --- 2. Adicionar Componentes (Registry) --- */}
        <div className="mb-6 mt-6 pt-6 border-t border-gray-100">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
            <Plus className="w-3 h-3 mr-1" />
            Adicionar
          </h4>

          {isLoadingRegistry ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : registry.length === 0 ? (
            <div className="text-center py-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded">
              Nenhum componente disponível
            </div>
          ) : (
            <div className="flex flex-col space-y-2">
              {registry
                .filter((item) => item.scope !== "global")
                .filter(
                  (item) =>
                    item.can_clone !== false ||
                    !hasPageInstanceForRegistryItem(item),
                )
                .map((item) => (
                  <button
                    key={item.name_id || item.name || `${item.component}-${item.scope || "page"}`}
                    onClick={() =>
                      !isLoading && addFromRegistry(item.name_id || item.name || item.component)
                    }
                    disabled={isLoading}
                    className="flex items-center w-full p-2 bg-white border border-gray-200 rounded-lg hover:border-primary-500 hover:shadow-sm transition-all group"
                  >
                    {/* Icone placeholder ou do registry se tiver */}
                    <div className="flex-shrink-0 w-4 h-4 bg-gray-50 rounded flex items-center justify-center mr-3 text-gray-300 group-hover:bg-primary-50 group-hover:text-primary-400">
                      <Plus className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 group-hover:text-primary-700 text-left">
                      {item.label || item.name_id || item.name || item.component}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
